import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IVirtualFencePolygon, defaultValue } from 'app/shared/model/virtual-fence-polygon.model';
import { markPendingDelete } from 'app/entities/virtual-fence/virtual-fence-list.reducer';

const initialState: EntityState<IVirtualFencePolygon> = {
  loading: false,
  loadingList: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/virtual-fence-polygons';

type VirtualFencePolygonQueryParams = IQueryParams & { companyId?: number };

const buildRequestUrl = ({ page, size, sort, companyId }: VirtualFencePolygonQueryParams = {}) => {
  const params = new URLSearchParams();
  if (page !== undefined) params.append('page', `${page}`);
  if (size !== undefined) params.append('size', `${size}`);
  if (sort) params.append('sort', sort);
  if (companyId !== undefined) params.append('companyId', `${companyId}`);
  params.append('cacheBuster', `${new Date().getTime()}`);
  return `${apiUrl}?${params.toString()}`;
};

export const getEntities = createAsyncThunk(
  'virtualFencePolygon/fetch_entity_list',
  async (params: VirtualFencePolygonQueryParams = {}) => {
    const requestUrl = buildRequestUrl(params);
    return axios.get<IVirtualFencePolygon[]>(requestUrl);
  }
);

export const getEntitiesForMap = createAsyncThunk(
  'virtualFencePolygon/fetch_entity_list_for_map',
  async () => axios.get<IVirtualFencePolygon[]>(`${apiUrl}/map`)
);

export const pollFences = createAsyncThunk(
  'virtualFencePolygon/poll_entity_list',
  async (params: VirtualFencePolygonQueryParams = {}) => {
    const requestUrl = buildRequestUrl(params);
    return axios.get<IVirtualFencePolygon[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'virtualFencePolygon/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IVirtualFencePolygon>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'virtualFencePolygon/create_entity',
  async (entity: IVirtualFencePolygon, thunkAPI) => {
    const result = await axios.post<IVirtualFencePolygon>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'virtualFencePolygon/update_entity',
  async (entity: IVirtualFencePolygon, thunkAPI) => {
    const result = await axios.put<IVirtualFencePolygon>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'virtualFencePolygon/partial_update_entity',
  async (entity: IVirtualFencePolygon, thunkAPI) => {
    const result = await axios.patch<IVirtualFencePolygon>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'virtualFencePolygon/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    await axios.delete<IVirtualFencePolygon>(requestUrl);
    const numericId = Number(id);
    thunkAPI.dispatch(markPendingDelete({ type: 'POLYGON', id: numericId }));
    return numericId;
  },
  { serializeError: serializeAxiosError }
);

export const VirtualFencePolygonSlice = createEntitySlice({
  name: 'virtualFencePolygon',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
        state.entities = state.entities.map(fence =>
          fence.id === deletedId ? { ...fence, syncStatus: 'PENDING_DELETE' as const } : fence
        );
      })
      .addCase(pollFences.pending, state => {
        state.loadingList = true;
      })
      .addCase(pollFences.fulfilled, (state, action) => {
        state.loadingList = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addCase(pollFences.rejected, state => {
        state.loadingList = false;
      })
      .addMatcher(isFulfilled(getEntities, getEntitiesForMap), (state, action) => ({
        ...state,
        loading: false,
        entities: action.payload.data,
        totalItems: parseInt(action.payload.headers['x-total-count'], 10),
      }))
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntitiesForMap, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = VirtualFencePolygonSlice.actions;

export default VirtualFencePolygonSlice.reducer;
